import { useMemo, useState } from 'react';
import { activePath, type ChatMessage, type CompiledContext, type Memory, type PinnedFact, type SegmentKind } from '@voicer/shared';
import { useStore } from '../state/store';
import { ws } from '../ws/client';
import { exportBackup, importBackup } from './backup';
import { importSillyTavernCard } from './importCard';

const KIND_LABEL: Record<string, string> = {
  template: 'template',
  system: 'system',
  pinned: 'pinned',
  memory: 'memories',
  summary: 'summary',
  retrieved: 'retrieved',
  turn: 'recent turns',
  aside: 'asides',
  current: 'current',
  recent: 'recent turns',
  reserve: 'reserve',
};

export function ContextInspector() {
  const session = useStore((s) => s.session);
  const compiled = useStore((s) => s.compiled);
  const dryRun = useStore((s) => s.dryRun);
  const setDryRun = useStore((s) => s.setDryRun);
  const sessions = useStore((s) => s.sessions);
  const [dryText, setDryText] = useState('');
  const [view, setView] = useState<'segments' | 'prompt'>('segments');
  const [busy, setBusy] = useState(false);

  const shown: CompiledContext | null = dryRun ?? compiled;

  const runDry = async () => {
    setBusy(true);
    try {
      setDryRun(await ws.rpc<CompiledContext>('context.dryrun', { text: dryText || '(next user message)' }));
    } finally {
      setBusy(false);
    }
  };

  const doExport = () => void exportBackup().catch(() => {});
  const doImport = (mode: 'new' | 'merge') => void importBackup(mode).catch(() => {});
  const doImportCard = (mode: 'new' | 'replacePersona') =>
    void importSillyTavernCard(mode).catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      alert(`Card import failed: ${msg}`);
    });

  if (!session) return <div className="panel-grid">Connecting…</div>;

  return (
    <div className="ctx">
      <aside className="ctx-side">
        <div className="panel">
          <h3>
            Session
            <span className="spacer" />
            <button
              className="small"
              title="New conversation (keeps memories & pinned)"
              onClick={() =>
                void ws.rpc('session.create', {
                  preserveMemories: true,
                  preservePinned: true,
                  preserveSystemPrompt: true,
                  preserveSummary: false,
                })
              }
            >
              new
            </button>
          </h3>
          <div className="row">
            <select value={session.session.id} onChange={(e) => void ws.rpc('session.switch', { id: e.target.value })} style={{ flex: 1 }}>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <button
              className="small"
              onClick={() => {
                const name = prompt('Session name', session.session.name);
                if (name) void ws.rpc('session.rename', { id: session.session.id, name });
              }}
            >
              rename
            </button>
            <button
              className="small danger"
              onClick={() => confirm('Delete this session and all its data?') && void ws.rpc('session.delete', { id: session.session.id })}
            >
              delete
            </button>
          </div>
          <div className="row" style={{ marginTop: 8 }}>
            <button className="small" onClick={doExport}>
              export JSON
            </button>
            <button className="small" onClick={() => doImport('new')}>
              import as new
            </button>
            <button className="small" onClick={() => doImport('merge')}>
              import into current
            </button>
            <button className="small danger" onClick={() => confirm('Clear all messages and the summary?') && void ws.rpc('session.clear')}>
              clear messages
            </button>
          </div>
          <div className="row" style={{ marginTop: 8 }}>
            <button
              className="small"
              title="Import a SillyTavern character card (.json or .png) as a new session"
              onClick={() => doImportCard('new')}
            >
              import card
            </button>
            <button
              className="small"
              title="Apply a SillyTavern card to the current session (system prompt, pinned PHI, lorebook memories)"
              onClick={() => doImportCard('replacePersona')}
            >
              apply card to current
            </button>
          </div>
          <div className="row" style={{ marginTop: 8 }}>
            <button
              className="small danger"
              title="Wipe messages, memories, pinned facts, and summary; set the expert legal advisor system prompt"
              onClick={() => {
                if (
                  !confirm(
                    'Start clean as an expert legal advisor?\n\nThis clears messages, memories, pinned facts, and the summary, then installs the legal-advisor system prompt.',
                  )
                ) {
                  return;
                }
                setBusy(true);
                void ws
                  .rpc('session.resetLegalAdvisor')
                  .then(() => setDryRun(null))
                  .finally(() => setBusy(false));
              }}
              disabled={busy}
            >
              reset · legal advisor
            </button>
          </div>
        </div>

        <SystemPromptEditor />
        <PinnedEditor pinned={session.pinned} />
        <MemoriesEditor memories={session.memories} />
        <SummaryEditor summary={session.summary} />
      </aside>

      <section className="ctx-main">
        <div className="panel">
          <h3>
            Compiled context {dryRun ? '(dry run)' : compiled ? '(last turn)' : ''}
            <span className="spacer" />
            <input type="text" placeholder="hypothetical next user message" value={dryText} onChange={(e) => setDryText(e.target.value)} style={{ width: 260 }} />
            <button className="small" disabled={busy} onClick={() => void runDry()}>
              dry-run compile
            </button>
            {dryRun && (
              <button className="small" onClick={() => setDryRun(null)}>
                show last turn
              </button>
            )}
            <button className={view === 'segments' ? 'small mode active' : 'small'} onClick={() => setView('segments')}>
              segments
            </button>
            <button className={view === 'prompt' ? 'small mode active' : 'small'} onClick={() => setView('prompt')}>
              exact prompt
            </button>
          </h3>
          {shown ? <BudgetBar c={shown} /> : <div className="dim">No compiled context yet. Send a message or run a dry-run compile.</div>}
        </div>
        {shown && view === 'segments' && <SegmentList c={shown} session={session} />}
        {shown && view === 'prompt' && <PromptPreview c={shown} />}
        <TurnsEditor messages={activePath(session.messages, session.activeLeafId)} excluded={session.excludedRetrieval} />
      </section>
    </div>
  );
}

function BudgetBar({ c }: { c: CompiledContext }) {
  const total = c.maxContext || 1;
  return (
    <div>
      <div className="budget" title={`${c.totalTokens} / ${c.budget} budget (${c.maxContext} max, ${c.tokenCountMode} counts)`}>
        {c.shares.map((s) => (
          <div key={s.kind} className={`seg-${s.kind}`} style={{ width: `${(s.used / total) * 100}%`, background: 'var(--c)' }} title={`${KIND_LABEL[s.kind]}: ${s.used} used / ${s.allocated} allocated`} />
        ))}
        <div style={{ width: `${((c.shares.find((s) => s.kind === 'reserve')?.allocated ?? 0) / total) * 100}%`, background: 'repeating-linear-gradient(45deg,#333 0 4px,#222 4px 8px)' }} title="generation reserve" />
      </div>
      <div className="legend">
        {c.shares.map((s) => (
          <span key={s.kind} className={`seg-${s.kind}`}>
            {KIND_LABEL[s.kind]} {s.used}/{s.allocated}
          </span>
        ))}
        <span className="dim" style={{ marginLeft: 'auto' }}>
          total {c.totalTokens} tok · budget {c.budget} · max {c.maxContext} · template {c.template} · compiled in {c.compileMs} ms · counts via {c.tokenCountMode}
        </span>
      </div>
    </div>
  );
}

function SegmentList({ c, session }: { c: CompiledContext; session: NonNullable<ReturnType<typeof useStore.getState>['session']> }) {
  const excluded = new Set(session.excludedRetrieval);
  const groups = useMemo(() => {
    const order: SegmentKind[] = ['template', 'system', 'pinned', 'memory', 'summary', 'retrieved', 'turn', 'aside', 'current'];
    return order.map((k) => ({ kind: k, items: c.segments.filter((s) => s.kind === k) })).filter((g) => g.items.length);
  }, [c]);
  return (
    <div className="stack">
      {groups.map((g) => (
        <div className="panel" key={g.kind}>
          <h3>
            {KIND_LABEL[g.kind]} <span className="dim">{g.items.reduce((s, x) => s + x.tokens, 0)} tok · {g.items.length} items</span>
          </h3>
          <div className="stack">
            {g.items.map((s) => (
              <div className={`segment seg-${s.kind}`} key={s.id}>
                <div className="head">
                  <b>{s.label}</b>
                  <span className="dim">{s.tokens} tok</span>
                  <span className="dim">{s.reason}</span>
                  {s.kind === 'retrieved' && (
                    <button className="link small" onClick={() => void ws.rpc('context.retrieval.exclude', { id: s.id, excluded: !excluded.has(s.id) })}>
                      {excluded.has(s.id) ? 'include again' : 'exclude from retrieval'}
                    </button>
                  )}
                </div>
                {s.text && <pre>{s.text}</pre>}
              </div>
            ))}
          </div>
        </div>
      ))}
      {c.dropped.length > 0 && (
        <div className="panel">
          <h3>
            Dropped <span className="dim">{c.dropped.length} items · {c.dropped.reduce((s, x) => s + x.tokens, 0)} tok</span>
          </h3>
          <table className="data">
            <tbody>
              {c.dropped.map((d) => (
                <tr key={d.id}>
                  <td>{KIND_LABEL[d.kind]}</td>
                  <td>{d.label}</td>
                  <td className="num">{d.tokens}</td>
                  <td className="dim">{d.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PromptPreview({ c }: { c: CompiledContext }) {
  // Color the exact prompt by locating each segment's text inside it.
  const parts = useMemo(() => {
    const spans: { start: number; end: number; kind: string; label: string }[] = [];
    let cursor = 0;
    for (const s of c.segments) {
      if (!s.text) continue;
      const idx = c.prompt.indexOf(s.text, cursor);
      if (idx < 0) continue;
      spans.push({ start: idx, end: idx + s.text.length, kind: s.kind, label: s.label });
      cursor = idx + s.text.length;
    }
    spans.sort((a, b) => a.start - b.start);
    const out: { text: string; kind: string | null; label?: string }[] = [];
    let pos = 0;
    for (const sp of spans) {
      if (sp.start > pos) out.push({ text: c.prompt.slice(pos, sp.start), kind: null });
      out.push({ text: c.prompt.slice(sp.start, sp.end), kind: sp.kind, label: sp.label });
      pos = sp.end;
    }
    if (pos < c.prompt.length) out.push({ text: c.prompt.slice(pos), kind: null });
    return out;
  }, [c]);
  return (
    <div className="panel">
      <h3>
        Exact prompt sent to KoboldCPP <span className="dim">{c.prompt.length} chars</span>
        <span className="spacer" />
        <button className="small" onClick={() => void navigator.clipboard.writeText(c.prompt)}>
          copy
        </button>
      </h3>
      <div className="prompt-preview">
        {parts.map((p, i) =>
          p.kind ? (
            <span key={i} className={`seg-${p.kind}`} title={p.label}>
              {p.text}
            </span>
          ) : (
            <span key={i} className="tag" style={{ borderBottom: 'none' }}>
              {p.text}
            </span>
          ),
        )}
      </div>
    </div>
  );
}

function SystemPromptEditor() {
  const session = useStore((s) => s.session)!;
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? session.systemPrompt;
  const dirty = draft !== null && draft !== session.systemPrompt;
  return (
    <div className="panel">
      <h3>
        System prompt <span className="dim">{session.promptVersions.length} versions</span>
        <span className="spacer" />
        <button className="small primary" disabled={!dirty} onClick={() => void ws.rpc('context.systemPrompt.set', { content: value }).then(() => setDraft(null))}>
          save as new version
        </button>
      </h3>
      <textarea value={value} onChange={(e) => setDraft(e.target.value)} style={{ minHeight: 140 }} />
      {session.promptVersions.length > 1 && (
        <div className="stack" style={{ marginTop: 6 }}>
          {session.promptVersions.slice(0, 8).map((v) => (
            <div className="item" key={v.id}>
              <div className="grow">
                <div className="meta">
                  {new Date(v.createdAt).toLocaleString()} {v.active ? '· active' : ''}
                </div>
                <div className="dim" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {v.content.slice(0, 90)}
                </div>
              </div>
              {!v.active && (
                <button className="small" onClick={() => void ws.rpc('context.systemPrompt.activate', { versionId: v.id })}>
                  revert
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PinnedEditor({ pinned }: { pinned: PinnedFact[] }) {
  const [text, setText] = useState('');
  const move = (i: number, dir: -1 | 1) => {
    const ids = pinned.map((p) => p.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    void ws.rpc('context.pinned.reorder', { ids });
  };
  return (
    <div className="panel">
      <h3>
        Pinned facts and persona <span className="dim">always included</span>
      </h3>
      {pinned.map((p, i) => (
        <div className="item" key={p.id}>
          <input type="text" defaultValue={p.text} onBlur={(e) => e.target.value !== p.text && void ws.rpc('context.pinned.update', { id: p.id, text: e.target.value })} />
          <button className="small" onClick={() => move(i, -1)}>
            ↑
          </button>
          <button className="small" onClick={() => move(i, 1)}>
            ↓
          </button>
          <button className="small danger" onClick={() => void ws.rpc('context.pinned.delete', { id: p.id })}>
            ×
          </button>
        </div>
      ))}
      <div className="row" style={{ marginTop: 6 }}>
        <input type="text" placeholder="e.g. The user's name is Matt and they prefer short answers." value={text} onChange={(e) => setText(e.target.value)} style={{ flex: 1 }} onKeyDown={(e) => e.key === 'Enter' && text.trim() && (void ws.rpc('context.pinned.add', { text }), setText(''))} />
        <button className="small" disabled={!text.trim()} onClick={() => (void ws.rpc('context.pinned.add', { text }), setText(''))}>
          add
        </button>
      </div>
    </div>
  );
}

function MemoriesEditor({ memories }: { memories: Memory[] }) {
  const [q, setQ] = useState('');
  const [text, setText] = useState('');
  const list = memories.filter((m) => !q || m.text.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="panel">
      <h3>
        Memories <span className="dim">{memories.length}</span>
        <span className="spacer" />
        <input type="text" placeholder="search" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 120 }} />
      </h3>
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {list.map((m) => (
          <div className="item" key={m.id}>
            <div className="grow">
              <textarea defaultValue={m.text} style={{ minHeight: 36 }} onBlur={(e) => e.target.value !== m.text && void ws.rpc('context.memory.update', { id: m.id, text: e.target.value })} />
              <div className="meta">
                salience{' '}
                <input type="number" step={0.1} min={0} max={5} defaultValue={m.salience.toFixed(2)} style={{ width: 60 }} onBlur={(e) => void ws.rpc('context.memory.update', { id: m.id, salience: Number(e.target.value) })} /> · used {m.refCount}x · {new Date(m.createdAt).toLocaleDateString()}
                {m.sourceTurnId ? ` · from turn ${m.sourceTurnId.slice(-6)}` : ' · manual'}
                {m.hasEmbedding ? ' · embedded' : ''}
              </div>
            </div>
            <button className={m.pinned ? 'small primary' : 'small'} title="Pinned memories never decay and rank first" onClick={() => void ws.rpc('context.memory.update', { id: m.id, pinned: !m.pinned })}>
              {m.pinned ? 'pinned' : 'pin'}
            </button>
            <button className="small danger" onClick={() => void ws.rpc('context.memory.delete', { id: m.id })}>
              ×
            </button>
          </div>
        ))}
        {!list.length && <div className="dim">No memories yet. They are extracted in the background after turns, or add one below.</div>}
      </div>
      <div className="row" style={{ marginTop: 6 }}>
        <input type="text" placeholder="add a memory" value={text} onChange={(e) => setText(e.target.value)} style={{ flex: 1 }} onKeyDown={(e) => e.key === 'Enter' && text.trim() && (void ws.rpc('context.memory.add', { text }), setText(''))} />
        <button className="small" disabled={!text.trim()} onClick={() => (void ws.rpc('context.memory.add', { text }), setText(''))}>
          add
        </button>
      </div>
    </div>
  );
}

function SummaryEditor({ summary }: { summary: string }) {
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const value = draft ?? summary;
  return (
    <div className="panel">
      <h3>
        Rolling summary
        <span className="spacer" />
        <button
          className="small"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await ws.rpc('context.summary.regenerate', {}, 180_000);
              setDraft(null);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'regenerating…' : 'regenerate now'}
        </button>
        <button className="small primary" disabled={draft === null || draft === summary} onClick={() => void ws.rpc('context.summary.set', { content: value }).then(() => setDraft(null))}>
          save
        </button>
      </h3>
      <textarea value={value} onChange={(e) => setDraft(e.target.value)} placeholder="Empty until turns fall out of the recent window (or regenerate now)." />
    </div>
  );
}

function TurnsEditor({ messages, excluded }: { messages: ChatMessage[]; excluded: string[] }) {
  const ex = new Set(excluded);
  return (
    <div className="panel">
      <h3>
        Active branch <span className="dim">{messages.length} messages · edit, exclude from prompt, redact, pin</span>
      </h3>
      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        {[...messages].reverse().map((m) => (
          <div className={`item ${m.excluded ? 'excluded' : ''}`} key={m.id}>
            <div className="grow">
              <div className="meta">
                #{m.seq} {m.role} · {new Date(m.ts).toLocaleTimeString()}
                {m.interrupted ? ' · interrupted' : ''}
                {m.excluded ? ' · excluded from prompt' : ''}
                {ex.has(m.id) ? ' · excluded from retrieval' : ''}
                {m.asideOutcome ? ` · ${m.asideOutcome.toLowerCase()}` : ''}
              </div>
              <textarea defaultValue={m.content} style={{ minHeight: 34 }} onBlur={(e) => e.target.value !== m.content && void ws.rpc('context.message.update', { id: m.id, patch: { content: e.target.value } })} />
              {m.interrupted && m.heardContent !== undefined && (
                <div className="meta">
                  heard: <input type="text" defaultValue={m.heardContent} style={{ width: '80%' }} onBlur={(e) => e.target.value !== m.heardContent && void ws.rpc('context.message.update', { id: m.id, patch: { heardContent: e.target.value } })} />
                </div>
              )}
            </div>
            <div className="stack">
              <button className="small" onClick={() => void ws.rpc('context.message.update', { id: m.id, patch: { excluded: !m.excluded } })}>
                {m.excluded ? 'include' : 'exclude'}
              </button>
              <button className="small" onClick={() => void ws.rpc('context.retrieval.exclude', { id: m.id, excluded: !ex.has(m.id) })}>
                {ex.has(m.id) ? 'retrievable' : 'no retrieval'}
              </button>
              <button className="small" onClick={() => void ws.rpc('context.message.update', { id: m.id, patch: { content: '[redacted]' } })}>
                redact
              </button>
              {m.role !== 'aside' && (
                <button className="small" onClick={() => void ws.rpc('context.message.toPinned', { id: m.id })}>
                  pin fact
                </button>
              )}
              <button className="small danger" onClick={() => void ws.rpc('context.message.delete', { id: m.id })}>
                delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
